<template>
  <div
    class="flex gap-2 items-end"
    :class="isOwn ? 'flex-row-reverse' : 'flex-row'">
    <!-- Avatar (only for other's messages) -->
    <UserAvatar
      v-if="!isOwn && message.sender"
      :user="message.sender"
      class="size-8 shrink-0" />

    <!-- Message content -->
    <div class="max-w-[75%] rounded-2xl px-4 py-2" :class="bubbleClasses">
      <!-- Reaction emoji (large, centered) -->
      <div v-if="isReaction" class="py-1 text-3xl text-center">
        {{ message.reaction_emoji }}
      </div>

      <!-- Shared content card -->
      <component
        :is="sharedContent.route ? 'router-link' : 'div'"
        v-if="sharedContent"
        :to="sharedContent.route || undefined"
        class="flex gap-2 items-center py-1"
        :class="{ 'hover:opacity-80 transition-opacity': sharedContent.route }">
        <img
          v-if="sharedContent.poster"
          :src="`https://image.tmdb.org/t/p/w92${sharedContent.poster}`"
          class="object-cover w-8 h-12 rounded shrink-0" />
        <div
          v-else
          class="flex justify-center items-center w-8 h-8 rounded bg-white/10 shrink-0">
          <fa-icon :icon="['far', sharedContent.icon]" class="text-xs" />
        </div>
        <span class="text-sm font-medium truncate">
          {{ sharedContent.label }}
        </span>
      </component>

      <!-- Text -->
      <p
        v-if="message.content"
        class="text-sm whitespace-pre-wrap break-words">
        {{ message.content }}
      </p>
    </div>

    <!-- Timestamp -->
    <span
      class="text-[10px] text-gray-500 self-end mb-1 shrink-0"
      :class="isOwn ? 'mr-1' : 'ml-1'">
      {{ formatTime(message.created_at) }}
    </span>
  </div>
</template>

<script setup>
import { computed } from 'vue'
import UserAvatar from '@/components/UserAvatar.vue'

const props = defineProps({
  message: {
    type: Object,
    required: true
  },
  isOwn: {
    type: Boolean,
    default: false
  },
  conversation: {
    type: Object,
    default: null
  }
})

const isReaction = computed(() => !!props.message.reaction_emoji)

// Shared title/review/list carried by this message. The conversation's
// joined content supplies the display name when the ids match.
const sharedContent = computed(() => {
  const message = props.message
  const conversation = props.conversation

  if (message.shared_review_id) {
    const review =
      conversation?.review?.id === message.shared_review_id
        ? conversation.review
        : null
    return {
      icon: 'star',
      label: review ? `Review: ${review.title_title}` : 'Shared a review',
      route: {
        name: 'WebReviewsSingle',
        params: { id: message.shared_review_id }
      }
    }
  }

  if (message.shared_title_id) {
    const title =
      conversation?.title?.id === message.shared_title_id
        ? conversation.title
        : null
    return {
      icon: 'film',
      poster: title?.tmdb_poster_path || null,
      label: title?.title || 'Recommended a title',
      route: {
        name: 'WebTitleDirect',
        params: { id: message.shared_title_id }
      }
    }
  }

  if (message.shared_list_id) {
    const list =
      conversation?.list?.id === message.shared_list_id
        ? conversation.list
        : null
    return {
      icon: 'rectangle-list',
      label: list ? `List: ${list.title}` : 'Shared a list',
      route: null
    }
  }

  return null
})

const bubbleClasses = computed(() => {
  if (isReaction.value) {
    return props.isOwn
      ? 'bg-indigo-600/20 border border-indigo-500/30'
      : 'bg-gray-800/50 border border-gray-700/50'
  }

  return props.isOwn
    ? 'bg-indigo-600 text-white rounded-br-md'
    : 'bg-gray-800 text-gray-100 rounded-bl-md'
})

const formatTime = (dateString) => {
  if (!dateString) return ''

  const date = new Date(dateString)
  const now = new Date()
  const diffMs = now - date
  const diffMins = Math.floor(diffMs / 60000)
  const diffHours = Math.floor(diffMs / 3600000)
  const diffDays = Math.floor(diffMs / 86400000)

  // Less than 1 minute ago
  if (diffMins < 1) return 'now'

  // Less than 1 hour ago
  if (diffMins < 60) return `${diffMins}m`

  // Less than 24 hours ago
  if (diffHours < 24) return `${diffHours}h`

  // Less than 7 days ago
  if (diffDays < 7) return `${diffDays}d`

  // Otherwise show date
  return date.toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric'
  })
}
</script>
